import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Dashboard = ({ setCurrentPage }) => {
  const [stats, setStats] = useState({
    totalDepartments: 0,
    totalTeachers: 0,
    totalClasses: 0,
    totalRooms: 0,
    totalCourses: 0,
    totalAcademicYears: 0,
    roomOccupancy: 0,
    activeTeachers: 0,
    scheduledClasses: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [departments, teachers, classes, rooms, courses, academicYears, sessions] = await Promise.all([
        axios.get('/api/departments'),
        axios.get('/api/teachers'),
        axios.get('/api/classes'),
        axios.get('/api/rooms'),
        axios.get('/api/courses'),
        axios.get('/api/academic-years'),
        axios.get('/api/sessions?limit=10000')
      ]);

      // Calculer les statistiques réelles
      const totalRooms = rooms.data.length;
      const totalTeachers = teachers.data.length;
      const totalClasses = classes.data.length;
      
      // Salles utilisées (ayant au moins une session)
      const usedRooms = new Set(sessions.data.filter(s => s.room).map(s => s.room._id)).size;
      const roomOccupancy = totalRooms > 0 ? Math.round((usedRooms / totalRooms) * 100) : 0;
      
      // Enseignants actifs (ayant au moins une session)
      const activeTeachers = new Set(sessions.data.filter(s => s.teacher).map(s => s.teacher._id)).size;
      const activeTeachersPercent = totalTeachers > 0 ? Math.round((activeTeachers / totalTeachers) * 100) : 0;
      
      // Classes programmées (ayant au moins une session)
      const scheduledClasses = new Set(sessions.data.map(s => s.class._id)).size;
      const scheduledClassesPercent = totalClasses > 0 ? Math.round((scheduledClasses / totalClasses) * 100) : 0;

      setStats({
        totalDepartments: departments.data.length,
        totalTeachers: totalTeachers,
        totalClasses: totalClasses,
        totalRooms: totalRooms,
        totalCourses: courses.data.length,
        totalAcademicYears: academicYears.data.length,
        roomOccupancy: roomOccupancy,
        activeTeachers: activeTeachersPercent,
        scheduledClasses: scheduledClassesPercent
      });
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
        <p className="text-sm text-gray-600">Chargement...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Tableau de bord</h1>
          <p className="text-gray-600">Vue d'ensemble de votre système de gestion d'emplois du temps</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🏢</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Départements</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalDepartments}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">👨🏫</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Enseignants</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalTeachers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🎓</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Classes</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalClasses}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🏫</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Salles</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalRooms}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">📚</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Cours</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalCourses}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">📅</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Années Univ.</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalAcademicYears}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Actions rapides</h3>
            <div className="space-y-3">
              <button 
                onClick={() => setCurrentPage('sessions')}
                className="w-full flex items-center justify-between p-3 text-left bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center">
                  <span className="text-lg mr-3">📅</span>
                  <span className="text-sm font-medium text-gray-900">Gérer les emplois</span>
                </div>
                <span className="text-gray-400">→</span>
              </button>
              <button 
                onClick={() => setCurrentPage('teachers')}
                className="w-full flex items-center justify-between p-3 text-left bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center">
                  <span className="text-lg mr-3">👨🏫</span>
                  <span className="text-sm font-medium text-gray-900">Ajouter un enseignant</span>
                </div>
                <span className="text-gray-400">→</span>
              </button>
              <button 
                onClick={() => setCurrentPage('classes')}
                className="w-full flex items-center justify-between p-3 text-left bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center">
                  <span className="text-lg mr-3">🎓</span>
                  <span className="text-sm font-medium text-gray-900">Créer une classe</span>
                </div>
                <span className="text-gray-400">→</span>
              </button>
              <button 
                onClick={() => setCurrentPage('pdf')}
                className="w-full flex items-center justify-between p-3 text-left bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center">
                  <span className="text-lg mr-3">📄</span>
                  <span className="text-sm font-medium text-gray-900">Générer Emplois</span>
                </div>
                <span className="text-gray-400">→</span>
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Statistiques système</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Taux d'occupation des salles</span>
                <span className="text-sm font-medium text-gray-900">{stats.roomOccupancy}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full" style={{width: `${stats.roomOccupancy}%`}}></div>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Enseignants actifs</span>
                <span className="text-sm font-medium text-gray-900">{stats.activeTeachers}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-green-600 h-2 rounded-full" style={{width: `${stats.activeTeachers}%`}}></div>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Classes programmées</span>
                <span className="text-sm font-medium text-gray-900">{stats.scheduledClasses}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-purple-600 h-2 rounded-full" style={{width: `${stats.scheduledClasses}%`}}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;